import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Workspace } from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import { Button } from '../components/Button';
import { Input, ErrorMessage } from '../components/Input';
import { Modal, ModalActions } from '../components/Modal';
import { PageSkeleton } from '../components/PageSkeleton';
import { useAuth } from '../hooks/useAuth';
import { useWorkspaces } from '../providers/WorkspaceProvider';
import { useBrandForm } from '../hooks/brand/useBrandForm';
import { useBrandLivePersist } from '../hooks/brand/useBrandLivePersist';
import { useAssetManagement } from '../hooks/workspacePage/useAssetManagement';
import { useBackgroundImageManagement } from '../hooks/workspacePage/useBackgroundImageManagement';
import { BrandProvider, BrandContextValue, BrandTabKey } from '../components/brand/brandShared';
import { BrandAiFillModal } from '../components/brand/BrandAiFillModal';
import { BrandTabOverview } from '../components/brand/BrandTabOverview';
import { BrandTabIdentity } from '../components/brand/BrandTabIdentity';
import { BrandTabVoiceContent } from '../components/brand/BrandTabVoiceContent';
import { BrandTabVisual } from '../components/brand/BrandTabVisual';
import { BrandTabAssets } from '../components/brand/BrandTabAssets';
import { BrandTabSources } from '../components/brand/BrandTabSources';
import { BrandTabManual } from '../components/brand/BrandTabManual';

// ─── Tabs ─────────────────────────────────────────────────────────────────────
const TABS: { key: BrandTabKey; label: string; icon: string }[] = [
  { key: 'overview', label: 'Overview', icon: '🏠' },
  { key: 'sources', label: 'Sources', icon: '📚' },
  { key: 'identity', label: 'Identity', icon: '📋' },
  { key: 'voice', label: 'Voice & Content', icon: '🗣️' },
  { key: 'visual', label: 'Visual & Logo', icon: '🎨' },
  { key: 'assets', label: 'Assets', icon: '🖼️' },
  { key: 'manual', label: 'Manual', icon: '📖' },
];

// ─── Styled ─────────────────────────────────────────────────────────────────
const Page = styled.div`
  padding-bottom: 96px; /* deja lugar a la sticky save bar */
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: flex-end;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const HeaderField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};

  &.grow { flex: 1; min-width: 240px; }
`;

const HeaderLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  letter-spacing: 1.5px;
  text-transform: uppercase;
`;

const NameInput = styled(Input)`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  min-width: 200px;
`;

const TabBar = styled.nav`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  overflow-x: auto;
`;

const TabButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: none;
  border: none;
  border-bottom: 2px solid ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  margin-bottom: -1px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme, $active }) => ($active ? theme.fontWeight.semibold : theme.fontWeight.medium)};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const TabIcon = styled.span`
  font-size: 16px;
`;

const SaveBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 50;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

// Centra el contenido de la barra para que los botones queden alineados con la
// columna de contenido (en vez de pegados al borde del viewport).
const SaveBarInner = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

// Banner prominente del toggle "aplicar mi paleta a todos los posts". Vive
// arriba de las tabs para que sea lo primero que el user ve. La fuente de verdad
// es la misma que el toggle de la tab Visual (live.forceBrandColors).
const BrandColorBanner = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
`;

const BannerCheckbox = styled.input`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  cursor: pointer;
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BannerTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const BannerHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const BannerSaved = styled.span`
  margin-left: auto;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.success};
`;

const SaveStatus = styled.span<{ $tone: 'saved' | 'dirty' | 'clean' }>`
  margin-right: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme, $tone }) =>
    $tone === 'saved' ? theme.colors.success
      : $tone === 'dirty' ? '#D97706'
        : theme.colors.textMuted};

  &::before {
    content: ${({ $tone }) => ($tone === 'dirty' ? "'●'" : "'✓'")};
    font-size: ${({ $tone }) => ($tone === 'dirty' ? '10px' : '12px')};
  }
`;

export function BrandPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { removeWorkspace } = useWorkspaces();
  const isAdmin = !!user?.isAdmin;

  const [activeTab, setActiveTab] = useState<BrandTabKey>('overview');
  const [workspace, setWorkspace] = useState<Workspace | null>(null);

  // Deep-link a un tab vía ?tab=sources (lo usa el selector "Generate" →
  // "Mejorar el brand"). Solo aplica si el valor es un tab válido.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TABS.some((tab) => tab.key === t)) {
      setActiveTab(t as BrandTabKey);
    }
  }, [searchParams]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Hooks de datos (la página los posee y los provee por contexto).
  const form = useBrandForm(workspaceId);
  const live = useBrandLivePersist(workspaceId, workspace);
  const assets = useAssetManagement(workspaceId);
  const backgrounds = useBackgroundImageManagement(workspaceId);

  // Carga del workspace (logo/postsCount/owned) — separado del brand-info.
  const reloadWorkspace = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const ws = await apiClient.get<Workspace>(`/api/workspaces/${workspaceId}`);
      setWorkspace(ws);
    } catch (err) {
      console.error('Failed to fetch workspace:', err);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    void reloadWorkspace();
    void assets.fetchAssets();
    void backgrounds.fetchBackgroundImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // owned: el listado lo setea; si viene undefined asumimos dueño (single-fetch).
  const isOwner = workspace?.owned !== false;

  // Aviso al salir con cambios sin guardar (cierre de pestaña / refresh).
  useEffect(() => {
    if (!form.isDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [form.isDirty]);

  // Navegación con guardia si hay cambios sin guardar.
  const leaveTo = useCallback((path: string) => {
    if (form.isDirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    navigate(path);
  }, [form.isDirty, navigate]);

  const handleDeleteWorkspace = async () => {
    if (!workspaceId) return;
    setIsDeleting(true);
    try {
      await apiClient.delete(`/api/workspaces/${workspaceId}`);
      removeWorkspace(workspaceId);
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to delete workspace:', err);
      alert('Failed to delete workspace. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const ctx: BrandContextValue = {
    activeTab,
    setActiveTab,
    workspaceId,
    workspace,
    isOwner,
    isAdmin,
    form,
    live,
    assets,
    backgrounds,
    reloadWorkspace,
    openDeleteModal: () => setShowDeleteModal(true),
  };

  if (form.isLoading || !workspace) {
    return <PageSkeleton />;
  }

  return (
    <BrandProvider value={ctx}>
      <Page>
        {/* Header — nombre del workspace + source URL (editables; van a la save bar). */}
        <Header>
          <HeaderField>
            <HeaderLabel>Brand</HeaderLabel>
            <NameInput
              value={form.formData.workspaceName}
              onChange={(e) => form.setField('workspaceName', e.target.value)}
              placeholder="Brand name"
            />
          </HeaderField>
          <HeaderField className="grow">
            <HeaderLabel>Source URL</HeaderLabel>
            <Input
              type="url"
              value={form.formData.sourceUrl ?? ''}
              onChange={(e) => form.setField('sourceUrl', e.target.value)}
              placeholder="https://your-brand.com"
            />
          </HeaderField>
        </Header>

        {form.error && <ErrorMessage style={{ display: 'block', marginBottom: 16 }}>{form.error}</ErrorMessage>}

        {/* Toggle prominente: aplicar la paleta de marca a TODOS los posts.
            Mismo estado/persistencia que el toggle de la tab Visual (live). */}
        <BrandColorBanner>
          <BannerCheckbox
            type="checkbox"
            checked={live.forceBrandColors}
            onChange={(e) => live.setForceBrandColors(e.target.checked)}
          />
          <BannerText>
            <BannerTitle>Apply my palette to all posts</BannerTitle>
            <BannerHint>
              On by default: your brand colors recolor every template, even fixed-palette ones.
              Turn off to keep each template&apos;s original colors.
            </BannerHint>
          </BannerText>
          {live.savedField === 'forceBrandColors' && <BannerSaved>✓ Saved</BannerSaved>}
        </BrandColorBanner>

        {/* Tab bar — semántica ARIA tablist + navegación con flechas. */}
        <TabBar role="tablist" aria-label="Brand sections">
          {TABS.map((tab, idx) => (
            <TabButton
              key={tab.key}
              type="button"
              role="tab"
              id={`brand-tab-${tab.key}`}
              aria-selected={activeTab === tab.key}
              aria-controls={`brand-panel-${tab.key}`}
              tabIndex={activeTab === tab.key ? 0 : -1}
              $active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
              onKeyDown={(e) => {
                if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
                e.preventDefault();
                const dir = e.key === 'ArrowRight' ? 1 : -1;
                setActiveTab(TABS[(idx + dir + TABS.length) % TABS.length].key);
              }}
            >
              <TabIcon aria-hidden="true">{tab.icon}</TabIcon>
              {tab.label}
            </TabButton>
          ))}
        </TabBar>

        {/* Tab activa. */}
        <div role="tabpanel" id={`brand-panel-${activeTab}`} aria-labelledby={`brand-tab-${activeTab}`}>
          {activeTab === 'overview' && <BrandTabOverview />}
          {activeTab === 'sources' && <BrandTabSources />}
          {activeTab === 'identity' && <BrandTabIdentity />}
          {activeTab === 'voice' && <BrandTabVoiceContent />}
          {activeTab === 'visual' && <BrandTabVisual />}
          {activeTab === 'assets' && <BrandTabAssets />}
          {activeTab === 'manual' && <BrandTabManual />}
        </div>
      </Page>

      {/* Modal de auto-relleno con IA (lo disparan los ✨ de cada tab). */}
      <BrandAiFillModal />

      {/* Sticky save bar — cubre TODOS los campos del form (incluso tabs inactivas). */}
      <SaveBar>
        <SaveBarInner>
          {form.success ? (
            <SaveStatus $tone="saved">Saved just now</SaveStatus>
          ) : form.isDirty ? (
            <SaveStatus $tone="dirty">Unsaved changes</SaveStatus>
          ) : (
            <SaveStatus $tone="clean">All changes saved</SaveStatus>
          )}
          <Button $variant="outline" onClick={() => leaveTo(`/workspace/${workspaceId}`)} disabled={form.isSaving}>
            Cancel
          </Button>
          <Button onClick={() => void form.save()} disabled={form.isSaving || !form.isDirty}>
            {form.isSaving ? 'Saving…' : 'Save'}
          </Button>
        </SaveBarInner>
      </SaveBar>

      {/* Delete-workspace modal (owner-only — el botón vive en la Danger Zone de Overview). */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => { if (!isDeleting) setShowDeleteModal(false); }}
        title="Delete Workspace?"
        titleVariant="error"
        maxWidth="450px"
      >
        <p style={{ lineHeight: 1.5, color: '#64748B' }}>
          Are you sure you want to delete &quot;{workspace.name}&quot;? This will permanently delete:
        </p>
        <ul style={{ marginTop: 12, paddingLeft: 20, color: '#64748B' }}>
          <li>All brand information and research</li>
          <li>All brand assets and images</li>
          <li>All generated posts ({workspace.postsCount || 0} posts)</li>
          <li>All export history</li>
        </ul>
        <strong style={{ display: 'block', marginTop: 12, color: '#ef4444' }}>
          This action cannot be undone.
        </strong>
        <ModalActions>
          <Button type="button" $variant="outline" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button type="button" $variant="danger" onClick={handleDeleteWorkspace} disabled={isDeleting}>
            {isDeleting ? 'Deleting…' : 'Delete Workspace'}
          </Button>
        </ModalActions>
      </Modal>
    </BrandProvider>
  );
}
